// A block a card names as parted is made whole again by moving the least that will do it,
// facings untouched:
// - met twice along a shelf, the stray piece or the foreign product between the pieces,
//   whichever is narrower, goes home, beside the products it shares the most of the tree with,
//   and where home has no room, to the edge of the block's main stretch;
// - parted by its own siblings, the parent reads its children in one order on every shelf;
// - standing in pieces on different shelves, a piece changes places with its siblings on a
//   shelf beside the rest of the block, so the block stands as one column.
// Each is a candidate: the caller judges it on the plan as drawn.
import {
  Flag,
  GeneratedLayout,
  GeneratedSegment,
  StudioDraft,
  StudioItem,
  StudioRunGeometry,
  effectiveAttrs,
  isPlaceholderValue,
  levelsForScope,
} from "./studioTypes";

export interface Position {
  skuId: string;
  index: number;
  run: number;
  shelf: number;
  xCm: number;
  widthCm: number;
  hFacings: number;
}

const WIDTH_EPS = 0.01;

const laneKeyOf = (run: number, shelf: number): string => `${run}|${shelf}`;

/** The values an item takes down its OWN branch of the tree, placeholders left out. */
const drawnPathOf = (item: StudioItem, draft: StudioDraft): string[] => {
  const attrs = effectiveAttrs(item, draft);
  const rootAttr = draft.spine[0]?.attr;
  const rootValue = rootAttr ? String(attrs[rootAttr] ?? "") : "";
  return levelsForScope(draft.spine, draft.scopedLevelOrder, [rootValue])
    .map((level) => String(attrs[level.attr] ?? ""))
    .filter((value) => !isPlaceholderValue(value));
};

const sharedDepth = (a: readonly string[], b: readonly string[]): number => {
  let depth = 0;
  while (depth < a.length && depth < b.length && a[depth] === b[depth]) depth += 1;
  return depth;
};

export const widthOf = (positions: readonly Position[]): number =>
  positions.reduce((sum, position) => sum + position.widthCm, 0);

/** Every position of the plan, lane by lane, left to right. */
export const lanesOf = (items: readonly StudioItem[]): Map<string, Position[]> => {
  const lanes = new Map<string, Position[]>();
  for (const item of items) {
    item.placements.forEach((placement, index) => {
      const key = laneKeyOf(placement.run, placement.shelf);
      const lane = lanes.get(key) ?? [];
      lane.push({
        skuId: item.skuId,
        index,
        run: placement.run,
        shelf: placement.shelf,
        xCm: placement.xCm,
        widthCm: placement.widthCm,
        hFacings: placement.hFacings,
      });
      lanes.set(key, lane);
    });
  }
  lanes.forEach((lane) => lane.sort((a, b) => a.xCm - b.xCm));
  return lanes;
};

/** The plan with `moved` positions in place of the items' own. */
export const layoutWith = (
  items: readonly StudioItem[],
  moved: ReadonlyMap<string, GeneratedSegment>,
): GeneratedLayout => {
  const placements: Record<string, { skuId: string; segments: GeneratedSegment[] }> = {};
  for (const item of items) {
    placements[item.skuId] = {
      skuId: item.skuId,
      segments: item.placements.map(
        (placement, index) =>
          moved.get(`${item.skuId}|${index}`) ?? {
            run: placement.run,
            shelf: placement.shelf,
            xCm: placement.xCm,
            hFacings: placement.hFacings,
          },
      ),
    };
  }
  const walkOrder = items
    .filter((item) => placements[item.skuId].segments.length > 0)
    .sort((a, b) => {
      const left = placements[a.skuId].segments[0];
      const right = placements[b.skuId].segments[0];
      return left.run - right.run || left.shelf - right.shelf || left.xCm - right.xCm;
    })
    .map((item) => item.skuId);
  return { placements, walkOrder };
};

/** The items standing where `layout` puts them, each position keeping its own width. */
const itemsWith = (items: readonly StudioItem[], layout: GeneratedLayout): StudioItem[] =>
  items.map((item) => ({
    ...item,
    placements: (layout.placements[item.skuId]?.segments ?? []).map((segment, index) => ({
      run: segment.run,
      shelf: segment.shelf,
      xCm: segment.xCm,
      hFacings: segment.hFacings,
      widthCm: item.placements[index]?.widthCm ?? segment.hFacings * item.facingWidthCm,
    })),
  }));

/**
 * A stretch of a lane read in a new order, in place: laid from the stretch's left edge, its
 * own air shared evenly between the joins, so the stretch keeps its extent and nothing outside
 * it moves. Nothing is recorded when the order is unchanged.
 */
export const relayStretch = (
  stretch: readonly Position[],
  sorted: readonly Position[],
  moved: Map<string, GeneratedSegment>,
): void => {
  if (sorted.every((position, slot) => position === stretch[slot])) return;
  const from = stretch[0].xCm;
  const to = stretch[stretch.length - 1].xCm + stretch[stretch.length - 1].widthCm;
  const air = Math.max(0, to - from - widthOf(stretch));
  const join = sorted.length > 1 ? air / (sorted.length - 1) : 0;
  let cursor = from;
  for (const position of sorted) {
    moved.set(`${position.skuId}|${position.index}`, {
      run: position.run,
      shelf: position.shelf,
      xCm: +cursor.toFixed(2),
      hFacings: position.hFacings,
    });
    cursor += position.widthCm + join;
  }
};

/**
 * `incoming` laid on a lane in place of `count` positions from `begin`: what stands left of
 * them stays put, the incoming stretch is laid tight from where the old one began, and what
 * stands right of it keeps its air, squeezed where the shelf would otherwise overrun.
 */
const relayLane = (
  original: readonly Position[],
  begin: number,
  count: number,
  incoming: readonly Position[],
  shelf: number,
  capacity: number,
  moved: Map<string, GeneratedSegment>,
): void => {
  const rest = original.slice(begin + count);
  const air = rest.map((position, index) => {
    const previous = index === 0 ? original[begin + count - 1] : rest[index - 1];
    return Math.max(0, position.xCm - (previous.xCm + previous.widthCm));
  });
  let cursor = original[begin].xCm;
  const tail = cursor + widthOf(incoming) + widthOf(rest);
  const totalAir = air.reduce((sum, gap) => sum + gap, 0);
  const squeeze = totalAir > 0 ? Math.max(0, Math.min(1, (capacity - tail) / totalAir)) : 1;
  const place = (position: Position) => {
    moved.set(`${position.skuId}|${position.index}`, {
      run: position.run,
      shelf,
      xCm: +cursor.toFixed(2),
      hFacings: position.hFacings,
    });
    cursor += position.widthCm;
  };
  incoming.forEach(place);
  rest.forEach((position, index) => {
    cursor += air[index] * squeeze;
    place(position);
  });
};

const startsWith = (path: readonly string[], prefix: readonly string[]): boolean =>
  prefix.length <= path.length && prefix.every((value, at) => path[at] === value);

/** Headroom over a shelf: up to the shelf above, or to the top of the run. */
const clearanceOf = (run: StudioRunGeometry, rank: number): number => {
  const shelf = run.shelves.find((entry) => entry.rank === rank);
  if (!shelf) return Infinity;
  const above = run.shelves.find((entry) => entry.rank === rank - 1);
  return (above ? above.yCm : run.heightCm) - shelf.yCm - shelf.thicknessCm;
};

/** Two facings on neighbouring shelves stand as one block when they overlap by a quarter pack,
 *  as the Aesthetics measure reads a block's shape. */
const OVERLAP_SHARE = 0.25;
/** The most positions one side of an exchange may hold. */
const MAX_EXCHANGED = 6;
/** Exchanges offered per piece and shelf, closest in width first. */
const EXCHANGES_PER_SHELF = 3;

/** What has to move for the block to stand in one stretch on this shelf. */
const moversOn = (lane: readonly Position[], members: ReadonlySet<string>): Position[] => {
  const stretches: { from: number; to: number }[] = [];
  lane.forEach((position, at) => {
    if (!members.has(position.skuId)) return;
    const last = stretches[stretches.length - 1];
    if (last && last.to === at - 1) last.to = at;
    else stretches.push({ from: at, to: at });
  });
  if (stretches.length < 2) return [];
  const slice = (from: number, to: number) => lane.slice(from, to + 1);
  const main = stretches.reduce((widest, stretch) =>
    widthOf(slice(stretch.from, stretch.to)) > widthOf(slice(widest.from, widest.to))
      ? stretch
      : widest,
  );
  const movers: Position[] = [];
  const mainAt = stretches.indexOf(main);
  // Outwards from the main stretch, so each piece is weighed against what parts it from
  // a block already whole up to there.
  const weigh = (piece: { from: number; to: number }, between: Position[]) => {
    const stray = slice(piece.from, piece.to);
    movers.push(...(widthOf(stray) <= widthOf(between) ? stray : between));
  };
  for (let at = mainAt - 1; at >= 0; at -= 1) {
    weigh(stretches[at], slice(stretches[at].to + 1, stretches[at + 1].from - 1));
  }
  for (let at = mainAt + 1; at < stretches.length; at += 1) {
    weigh(stretches[at], slice(stretches[at - 1].to + 1, stretches[at].from - 1));
  }
  return movers;
};

/**
 * The layout with the block named by `memberSkuIds` standing in one stretch on every shelf
 * it is met twice on, or null when no shelf parts it. Facings are never changed: positions
 * move, whole, and each shelf touched is spread across its width again.
 */
export const gatherScatteredBlock = (
  items: readonly StudioItem[],
  runs: readonly StudioRunGeometry[],
  draft: StudioDraft,
  memberSkuIds: readonly string[],
  /** The order the tree walks the products in. Given, a position that goes home takes its
   *  own place among its siblings; without it, it joins the end of their stretch. */
  treeOrder: readonly string[] = [],
): GeneratedLayout | null => {
  const members = new Set(memberSkuIds);
  const walkRank = new Map(treeOrder.map((skuId, rank) => [skuId, rank]));
  const pathOf = new Map(items.map((item) => [item.skuId, drawnPathOf(item, draft)]));
  const widthOfRun = new Map(runs.map((run) => [run.run, run.widthCm]));

  const lanes = lanesOf(items);

  const movers = [...lanes.values()].flatMap((lane) => moversOn(lane, members));
  if (movers.length === 0) return null;
  const moving = new Set(movers);
  const touched = new Set<string>();

  for (const mover of movers) {
    const fromKey = laneKeyOf(mover.run, mover.shelf);
    const from = lanes.get(fromKey) ?? [];
    const path = pathOf.get(mover.skuId) ?? [];
    from.splice(from.indexOf(mover), 1);
    touched.add(fromKey);

    // Home: the standing positions it shares the most of the tree with, on a shelf of
    // this run that has the room; between equals, the shelf with the most room.
    const homes: { key: string; at: number; depth: number; room: number }[] = [];
    for (const [key, lane] of lanes) {
      if (key !== fromKey && lane[0]?.run !== mover.run) continue;
      const room = (widthOfRun.get(mover.run) ?? 0) - widthOf(lane);
      if (room + WIDTH_EPS < mover.widthCm) continue;
      lane.forEach((position, at) => {
        if (moving.has(position) || position.skuId === mover.skuId) return;
        const depth = sharedDepth(path, pathOf.get(position.skuId) ?? []);
        if (depth >= 1) homes.push({ key, at, depth, room });
      });
    }
    // The LAST of the best: it joins the end of the stretch it belongs to.
    const home = homes.reduce<(typeof homes)[number] | null>(
      (best, candidate) =>
        !best ||
        candidate.depth > best.depth ||
        (candidate.depth === best.depth &&
          (candidate.room > best.room || (candidate.key === best.key && candidate.at > best.at)))
          ? candidate
          : best,
      null,
    );
    if (home) {
      const lane = lanes.get(home.key) ?? [];
      const shelf = lane[home.at].shelf;
      // Among the siblings it joins, before the first the tree walks after it.
      const rank = walkRank.get(mover.skuId);
      const later =
        rank === undefined
          ? -1
          : lane.findIndex((position) => {
              if (sharedDepth(path, pathOf.get(position.skuId) ?? []) < home.depth) return false;
              return (walkRank.get(position.skuId) ?? -1) > rank;
            });
      lane.splice(later >= 0 ? later : home.at + 1, 0, { ...mover, shelf });
      moving.delete(mover);
      touched.add(home.key);
      continue;
    }
    // Nowhere has the room: it stays on its shelf, at the edge of the block it left.
    const held = from.flatMap((position, at) => (members.has(position.skuId) ? [at] : []));
    const last = held.length > 0 ? held[held.length - 1] : from.length - 1;
    from.splice(last + 1, 0, mover);
    moving.delete(mover);
  }

  // Each shelf touched reads left to right in its new order, spread across its width.
  const moved = new Map<string, GeneratedSegment>();
  for (const key of touched) {
    const lane = lanes.get(key) ?? [];
    const used = widthOf(lane);
    const width = widthOfRun.get(lane[0]?.run ?? -1) ?? used;
    const spread = used > 0 && used < width ? width / used : 1;
    let cursor = 0;
    for (const position of lane) {
      moved.set(`${position.skuId}|${position.index}`, {
        run: position.run,
        shelf: position.shelf,
        xCm: +(cursor * spread).toFixed(2),
        hFacings: position.hFacings,
      });
      cursor += position.widthCm;
    }
  }

  return layoutWith(items, moved);
};

/**
 * A block parted by its own siblings is whole again when its parent reads its children in
 * the same order on every shelf: each child is then one stretch per shelf, and the stretches
 * stand over one another as a column. Only the parent's own stretches are reordered, in place,
 * so no shelf gains or loses width and nothing outside the parent moves.
 *
 * One plan per child order worth trying (see `readChildrenAlike`). A plan that moves nothing is
 * not offered.
 */
export const orderSiblingsAlike = (
  items: readonly StudioItem[],
  draft: StudioDraft,
  blockPath: readonly string[],
  treeOrder: readonly string[] = [],
): GeneratedLayout[] => {
  if (blockPath.length < 2) return [];
  const parent = blockPath.slice(0, -1);
  const depth = parent.length;
  const childOf = new Map<string, string>();
  for (const item of items) {
    const path = drawnPathOf(item, draft);
    if (path.length > depth && startsWith(path, parent)) childOf.set(item.skuId, path[depth]);
  }
  return readChildrenAlike(items, childOf, treeOrder);
};

/**
 * A parent's stretches on every shelf, each read with its children (`childOf`, keyed by product)
 * in one order: the order the tree walks them in (`treeOrder`, where given), the order they
 * already stand in across the wall, and, where `turned`, that order turned round, which stands
 * each child on the other side of its siblings. A plan that moves nothing is not offered.
 */
export const readChildrenAlike = (
  items: readonly StudioItem[],
  childOf: ReadonlyMap<string, string>,
  treeOrder: readonly string[] = [],
  turned = false,
): GeneratedLayout[] => {
  if (new Set(childOf.values()).size < 2) return [];
  const lanes = lanesOf(items);

  // The children in the order the tree walks them, and in the order they stand along the wall.
  const walkRank = new Map(treeOrder.map((skuId, rank) => [skuId, rank]));
  const firstWalked = new Map<string, number>();
  const centre = new Map<string, { moment: number; width: number }>();
  for (const lane of lanes.values()) {
    for (const position of lane) {
      const child = childOf.get(position.skuId);
      if (child === undefined) continue;
      const rank = walkRank.get(position.skuId);
      if (rank !== undefined)
        firstWalked.set(child, Math.min(firstWalked.get(child) ?? rank, rank));
      const entry = centre.get(child) ?? { moment: 0, width: 0 };
      entry.moment += (position.xCm + position.widthCm / 2) * position.widthCm;
      entry.width += position.widthCm;
      centre.set(child, entry);
    }
  }
  const children = [...centre.keys()];
  const byStanding = [...children].sort((a, b) => {
    const ca = centre.get(a)!;
    const cb = centre.get(b)!;
    return ca.moment / ca.width - cb.moment / cb.width;
  });
  const orders = turned ? [byStanding, [...byStanding].reverse()] : [byStanding];
  if (children.every((child) => firstWalked.has(child))) {
    orders.unshift(
      [...children].sort((a, b) => (firstWalked.get(a) ?? 0) - (firstWalked.get(b) ?? 0)),
    );
  }

  const plans: GeneratedLayout[] = [];
  const seen = new Set<string>();
  for (const order of orders) {
    const rankOf = new Map(order.map((child, rank) => [child, rank]));
    const moved = new Map<string, GeneratedSegment>();
    for (const lane of lanes.values()) {
      let at = 0;
      while (at < lane.length) {
        if (!childOf.has(lane[at].skuId)) {
          at += 1;
          continue;
        }
        let end = at;
        while (end + 1 < lane.length && childOf.has(lane[end + 1].skuId)) end += 1;
        const stretch = lane.slice(at, end + 1);
        const sorted = stretch
          .map((position, slot) => ({ position, slot }))
          .sort(
            (a, b) =>
              (rankOf.get(childOf.get(a.position.skuId)!) ?? 0) -
                (rankOf.get(childOf.get(b.position.skuId)!) ?? 0) || a.slot - b.slot,
          )
          .map((entry) => entry.position);
        relayStretch(stretch, sorted, moved);
        at = end + 1;
      }
    }
    if (moved.size === 0) continue;
    const signature = [...moved.entries()].map(([key, segment]) => `${key}@${segment.xCm}`).join();
    if (seen.has(signature)) continue;
    seen.add(signature);
    plans.push(layoutWith(items, moved));
  }
  return plans;
};

/** The widest of the pieces. */
export const widestOf = (pieces: readonly Position[][]): Position[] =>
  pieces.reduce((widest, piece) => (widthOf(piece) > widthOf(widest) ? piece : widest));

/**
 * A block's positions on one run, in the pieces the Aesthetics measure reads: along a shelf
 * with nothing foreign between, across neighbouring shelves where they overlap by a quarter
 * pack. Each piece lists its positions shelf by shelf, left to right.
 */
export const piecesOn = (
  lanes: ReadonlyMap<string, Position[]>,
  run: StudioRunGeometry,
  isMember: (skuId: string) => boolean,
  packWidthOf: (skuId: string) => number | undefined,
): Position[][] => {
  const laneOf = (rank: number) => lanes.get(laneKeyOf(run.run, rank)) ?? [];
  const members = run.shelves.flatMap((shelf) =>
    laneOf(shelf.rank).filter((position) => isMember(position.skuId)),
  );
  const at = new Map(members.map((position, index) => [position, index]));
  const rootOf = members.map((_, index) => index);
  const find = (index: number): number =>
    rootOf[index] === index ? index : (rootOf[index] = find(rootOf[index]));
  const join = (a: number, b: number) => {
    rootOf[find(a)] = find(b);
  };
  for (const shelf of run.shelves) {
    const lane = laneOf(shelf.rank);
    lane.forEach((position, index) => {
      if (index > 0 && isMember(position.skuId) && isMember(lane[index - 1].skuId)) {
        join(at.get(position)!, at.get(lane[index - 1])!);
      }
    });
    const below = laneOf(shelf.rank + 1).filter((position) => isMember(position.skuId));
    for (const upper of lane.filter((position) => isMember(position.skuId))) {
      for (const lower of below) {
        const overlap =
          Math.min(upper.xCm + upper.widthCm, lower.xCm + lower.widthCm) -
          Math.max(upper.xCm, lower.xCm);
        const pack = Math.min(
          packWidthOf(upper.skuId) ?? upper.widthCm,
          packWidthOf(lower.skuId) ?? lower.widthCm,
        );
        if (overlap > OVERLAP_SHARE * pack) join(at.get(upper)!, at.get(lower)!);
      }
    }
  }
  const pieces = new Map<number, Position[]>();
  members.forEach((position, index) => {
    const root = find(index);
    pieces.set(root, [...(pieces.get(root) ?? []), position]);
  });
  return [...pieces.values()];
};

/**
 * A block standing in pieces on different shelves is stood up as one column: a piece that
 * touches the rest of its block nowhere changes places with products of the same parent on a
 * shelf beside the block's main piece, so it lands over or under that piece and what it
 * displaced takes its old place. Only siblings are exchanged, so the parent keeps its
 * footprint; nothing is exchanged onto a shelf it does not fit along. Where the stray lands
 * among its siblings rather than against its block, the parent's children are then read
 * alike on every shelf, which stands it beside the rest.
 *
 * One plan per exchange worth trying; none when the block already stands in one piece.
 */
export const standBlockAsColumn = (
  items: readonly StudioItem[],
  runs: readonly StudioRunGeometry[],
  draft: StudioDraft,
  blockPath: readonly string[],
): GeneratedLayout[] => {
  // A top-level block's siblings are other sections: exchanging with them reorders the aisle.
  if (blockPath.length < 2) return [];
  const parent = blockPath.slice(0, -1);
  const pathOf = new Map(items.map((item) => [item.skuId, drawnPathOf(item, draft)]));
  const itemOf = new Map(items.map((item) => [item.skuId, item]));
  const isMember = (skuId: string) => startsWith(pathOf.get(skuId) ?? [], blockPath);
  const isSibling = (skuId: string) =>
    !isMember(skuId) && startsWith(pathOf.get(skuId) ?? [], parent);
  const lanes = lanesOf(items);
  const plans: GeneratedLayout[] = [];
  const tallest = (positions: readonly Position[]) =>
    Math.max(...positions.map((position) => itemOf.get(position.skuId)?.heightCm ?? 0));

  for (const run of runs) {
    const laneOf = (rank: number) => lanes.get(laneKeyOf(run.run, rank)) ?? [];
    const pieces = piecesOn(lanes, run, isMember, (skuId) => itemOf.get(skuId)?.facingWidthCm);
    if (pieces.length < 2) continue;
    const main = widestOf(pieces);
    const mainShelves = new Set(main.map((position) => position.shelf));

    for (const piece of pieces) {
      if (piece === main) continue;
      // A piece in one stretch on one shelf is what can change places whole.
      const from = piece[0].shelf;
      if (piece.some((position) => position.shelf !== from)) continue;
      const fromLane = laneOf(from);
      const first = fromLane.indexOf(piece[0]);
      const stray = fromLane.slice(first, first + piece.length);
      if (stray.some((position) => !piece.includes(position))) continue;
      const strayWidth = widthOf(stray);

      const targets = new Set<number>();
      for (const shelf of mainShelves) {
        for (const rank of [shelf - 1, shelf, shelf + 1]) {
          if (rank !== from && run.shelves.some((entry) => entry.rank === rank)) targets.add(rank);
        }
      }
      for (const target of targets) {
        // By the pack's own height; the caller checks the stack as drawn.
        if (tallest(stray) > clearanceOf(run, target)) continue;
        const lane = laneOf(target);
        const room = run.widthCm - widthOf(lane);
        const fromRoom = run.widthCm - widthOf(fromLane);
        // Where the stray would land: beside the main piece on its own shelf, or over or
        // under it on the next.
        const lands = (start: number, end: number): boolean => {
          if (mainShelves.has(target)) {
            return main.some((position) => {
              const index = lane.indexOf(position);
              return index === start - 1 || index === end + 1;
            });
          }
          const x = lane[start].xCm;
          return main.some((position) => {
            if (Math.abs(position.shelf - target) !== 1) return false;
            const overlap =
              Math.min(x + strayWidth, position.xCm + position.widthCm) - Math.max(x, position.xCm);
            return overlap > OVERLAP_SHARE * (itemOf.get(position.skuId)?.facingWidthCm ?? 0);
          });
        };
        const exchanges: { start: number; end: number; width: number; lands: boolean }[] = [];
        for (let start = 0; start < lane.length; start += 1) {
          let width = 0;
          for (let end = start; end < lane.length && end - start < MAX_EXCHANGED; end += 1) {
            if (!isSibling(lane[end].skuId)) break;
            width += lane[end].widthCm;
            const fits =
              width - strayWidth <= fromRoom + WIDTH_EPS && strayWidth - width <= room + WIDTH_EPS;
            if (!fits) continue;
            if (tallest(lane.slice(start, end + 1)) > clearanceOf(run, from)) continue;
            exchanges.push({ start, end, width, lands: lands(start, end) });
          }
        }
        exchanges
          .sort(
            (a, b) =>
              Number(b.lands) - Number(a.lands) ||
              Math.abs(a.width - strayWidth) - Math.abs(b.width - strayWidth),
          )
          .slice(0, EXCHANGES_PER_SHELF)
          .forEach(({ start, end, lands: landsWhole }) => {
            const displaced = lane.slice(start, end + 1);
            const moved = new Map<string, GeneratedSegment>();
            relayLane(fromLane, first, stray.length, displaced, from, run.widthCm, moved);
            relayLane(lane, start, displaced.length, stray, target, run.widthCm, moved);
            const exchanged = layoutWith(items, moved);
            if (landsWhole) plans.push(exchanged);
            plans.push(...orderSiblingsAlike(itemsWith(items, exchanged), draft, blockPath));
          });
      }
    }
  }
  return plans;
};

/**
 * A block standing in pieces is whole again when a piece slides along a shelf to stand over,
 * under or beside the rest of the block: what the piece has on that shelf leaves its place among
 * the products of its parent and is laid back between two of its siblings, the stretch between
 * keeping its extent, so no pack changes shelf, no facing changes, and nothing outside that
 * stretch moves. `siblingOf` names the block a product stands in among the block's siblings, or
 * nothing outside its parent (a top-level block's parent is the whole shelf).
 *
 * One plan per place a piece's shelf lands against the rest of its block, with what it slides.
 */
export const slidePieceAlongShelf = (
  items: readonly StudioItem[],
  runs: readonly StudioRunGeometry[],
  isMember: (skuId: string) => boolean,
  siblingOf: (skuId: string) => string | undefined,
): { layout: GeneratedLayout; piece: Position[] }[] => {
  const within = (skuId: string) => siblingOf(skuId) !== undefined;
  const packWidth = new Map(items.map((item) => [item.skuId, item.facingWidthCm]));
  const lanes = lanesOf(items);
  const plans: { layout: GeneratedLayout; piece: Position[] }[] = [];
  for (const run of runs) {
    const pieces = piecesOn(lanes, run, isMember, (skuId) => packWidth.get(skuId));
    if (pieces.length < 2) continue;
    for (const piece of pieces) {
      const others = pieces.filter((other) => other !== piece).flat();
      for (const shelf of new Set(piece.map((position) => position.shelf))) {
        const lane = lanes.get(laneKeyOf(run.run, shelf)) ?? [];
        const part = piece.filter((position) => position.shelf === shelf);
        // What slides is one stretch: the parent's stretch around it is where it may go.
        let from = lane.indexOf(part[0]);
        if (part.some((position, at) => lane[from + at] !== position)) continue;
        let to = from + part.length - 1;
        while (from > 0 && within(lane[from - 1].skuId)) from -= 1;
        while (to + 1 < lane.length && within(lane[to + 1].skuId)) to += 1;
        const stretch = lane.slice(from, to + 1);
        const rest = stretch.filter((position) => !part.includes(position));
        for (let at = 0; at <= rest.length; at += 1) {
          // Never into the middle of a sibling.
          if (
            at > 0 &&
            at < rest.length &&
            siblingOf(rest[at - 1].skuId) === siblingOf(rest[at].skuId)
          ) {
            continue;
          }
          const order = [...rest.slice(0, at), ...part, ...rest.slice(at)];
          const moved = new Map<string, GeneratedSegment>();
          relayStretch(stretch, order, moved);
          if (moved.size === 0) continue;
          const besideOnShelf = [order[at - 1], order[at + part.length]].some(
            (position) => position !== undefined && isMember(position.skuId),
          );
          const landsOver = part.some((position) => {
            const x = moved.get(`${position.skuId}|${position.index}`)?.xCm ?? position.xCm;
            return others.some((other) => {
              if (Math.abs(other.shelf - shelf) !== 1) return false;
              const overlap =
                Math.min(x + position.widthCm, other.xCm + other.widthCm) - Math.max(x, other.xCm);
              const pack = Math.min(
                packWidth.get(position.skuId) ?? position.widthCm,
                packWidth.get(other.skuId) ?? other.widthCm,
              );
              return overlap > OVERLAP_SHARE * pack;
            });
          });
          if (besideOnShelf || landsOver) {
            plans.push({ layout: layoutWith(items, moved), piece: part });
          }
        }
      }
    }
  }
  return plans;
};

/**
 * Every plan the studio can offer to make the block a card names whole again: the stray piece
 * gathered along its shelf, the parent's children read alike on every shelf, a piece stood up
 * over or under the rest of its block, a piece slid along its shelf to stand with the rest.
 * Each is a candidate only; the caller judges them on the plan as drawn.
 */
export const blockGatherings = (
  items: readonly StudioItem[],
  runs: readonly StudioRunGeometry[],
  draft: StudioDraft,
  flag: Pick<Flag, "kind" | "scopePath" | "skuIds">,
  treeOrder: readonly string[] = [],
): GeneratedLayout[] => {
  if (flag.kind !== "split" && flag.kind !== "contiguity") return [];
  // A card's scope can end in words that are no level of the tree ("shelf 4").
  const paths = items.map((item) => drawnPathOf(item, draft));
  const pathByItem = new Map(items.map((item, at) => [item.skuId, paths[at]]));
  let depth = flag.scopePath.length;
  while (depth > 0 && !paths.some((path) => startsWith(path, flag.scopePath.slice(0, depth)))) {
    depth -= 1;
  }
  const blockPath = flag.scopePath.slice(0, depth);
  const plans: GeneratedLayout[] = [];
  // A split card lists the block's own products; a contiguity card lists the intruders.
  if (flag.kind === "split") {
    const gathered = gatherScatteredBlock(items, runs, draft, flag.skuIds, treeOrder);
    if (gathered) plans.push(gathered);
  }
  if (blockPath.length === 0) return plans;
  plans.push(...orderSiblingsAlike(items, draft, blockPath, treeOrder));
  plans.push(...standBlockAsColumn(items, runs, draft, blockPath));
  const parent = blockPath.slice(0, -1);
  const slides = slidePieceAlongShelf(
    items,
    runs,
    (skuId) => startsWith(pathByItem.get(skuId) ?? [], blockPath),
    (skuId) => {
      const path = pathByItem.get(skuId) ?? [];
      return startsWith(path, parent) ? path[parent.length] : undefined;
    },
  );
  plans.push(...slides.map((slide) => slide.layout));
  return plans;
};
